import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Listing {
  id: string;
  ownerId: string;
  title: string;
  description: string;
  location: string;
  displayLocation: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  available: string;
  endDate: string;
  images: string[];
  amenities: string[];
  status: 'active' | 'pending' | 'inactive';
  isLeased: boolean;
  applicantCriteria?: {
    minimumIncome: string;
    minimumCreditScore: string;
    requireBackgroundCheck: boolean;
    requireReferences: boolean;
    referencesCount: string;
    employmentVerification: boolean;
    rentalHistory: boolean;
    preferredOccupations: string[];
    additionalRequirements: string;
  };
}

interface ListingStore {
  listings: Listing[];
  favorites: string[];
  addListing: (listing: Listing) => void;
  removeListing: (id: string) => void;
  updateListing: (id: string, updates: Partial<Listing>) => void;
  toggleFavorite: (listingId: string) => void;
  isFavorite: (listingId: string) => boolean;
  resetStore: () => void;
}

const useListingStore = create<ListingStore>()(
  persist(
    (set, get) => ({
      listings: [],
      favorites: [],
      
      addListing: (listing) => 
        set((state) => ({
          listings: [...state.listings, listing]
        })),
        
      removeListing: (id) =>
        set((state) => ({
          listings: state.listings.filter(listing => listing.id !== id)
        })),
        
      updateListing: (id, updates) =>
        set((state) => ({
          listings: state.listings.map(listing =>
            listing.id === id ? { ...listing, ...updates } : listing
          )
        })),

      toggleFavorite: (listingId) =>
        set((state) => ({
          favorites: state.favorites.includes(listingId)
            ? state.favorites.filter(id => id !== listingId)
            : [...state.favorites, listingId]
        })),

      isFavorite: (listingId) => get().favorites.includes(listingId),

      resetStore: () => set({ listings: [], favorites: [] })
    }),
    {
      name: 'listing-store',
      partialize: (state) => ({
        listings: state.listings,
        favorites: state.favorites
      })
    }
  )
);

export { useListingStore, type Listing, type ListingStore };